import { ActivityType } from '@showbie-socrative/socrative-utils';
import { QuestionTypes as QuestionTypesUtils } from '@showbie-socrative/socrative-utils';

import { CreateFolderEventAdapter } from '~analytics/adapters/CreateFolderEventAdapter';
import { GradeModifiedEventAdapter } from '~analytics/adapters/GradeModifiedEventAdapter';
import { ActivityState } from '~analytics/adapters/QuestionViewEventAdapter';
import { PassRateAction } from '~analytics/adapters/ReportStats/PassRateEventAdapter';
import { ViewInsightParticipantsEventAdapter } from '~analytics/adapters/ViewInsightParticipantsEventAdapter';

import { ContactSalesEventAdapter } from './adapters/ContactSalesEventAdapter';
import { CreateQuizEventAdapter } from './adapters/CreateQuizEventAdapter';
import { CreateWorkspaceEventAdapter } from './adapters/CreateWorkspaceEventAdapter';
import { ExportAnswerKeyEventAdapter } from './adapters/ExportAnswerKeyEventAdapter';
import { FinishActivityEventAdapter } from './adapters/FinishActivityEventAdapter';
import { GenerateAIQuestionsEventAdapter } from './adapters/GenerateAIQuestionsEventAdapter';
import { GenerateReportEmailStudentsEventAdapter } from './adapters/GenerateReportEmailStudentsEventAdapter';
import { GenerateReportEventAdapter } from './adapters/GenerateReportEventAdapter';
import { ImportFromTextEventAdapter } from './adapters/ImportFromTextEventAdapter';
import { JoinWorkspaceEventAdapter } from './adapters/JoinWorkspaceEventAdapter';
import { LaunchExitTicketEventAdapter } from './adapters/LaunchExitTicketEventAdapter';
import { LaunchMultipleChoiceEventAdapter } from './adapters/LaunchMultipleChoiceEventAdapter';
import { LaunchQuizEventAdapter } from './adapters/LaunchQuizEventAdapter';
import { LaunchShortAnswerEventAdapter } from './adapters/LaunchShortAnswerEventAdapter';
import { LaunchSpaceRaceEventAdapter } from './adapters/LaunchSpaceRaceEventAdapter';
import { LaunchTrueFalseEventAdapter } from './adapters/LaunchTrueFalseEventAdapter';
import { LaunchVoteEventAdapter } from './adapters/LaunchVoteEventAdapter';
import { LicenseApplyEventAdapter } from './adapters/LicenseApplyEventAdapter';
import { LicensePurchaseEventAdapter } from './adapters/LicensePurchaseEventAdapter';
import { PaywallTouchEventAdapter } from './adapters/PaywallTouchEventAdapter';
import { QuestionSavedEventAdapter } from './adapters/QuestionSavedEventAdapter';
import { sendTeacherAmplitudeEvent } from './amplitude';
import { EventTypes, QuestionTypes, RoomTypes } from './types';

// Account events
function sendSignUpEvent(data = {}) {
  sendTeacherAmplitudeEvent(EventTypes.SignUp, data);
}

// Launch events
function sendLaunchQuizEvent(data: {
  room_type: RoomTypes;
  quiz_type: 'instant_feedback' | 'open_navigation' | 'teacher_paced';
  activity_require_names: boolean;
  activity_shuffle_questions: boolean;
  activity_shuffle_answers: boolean;
  activity_show_question_feedback: boolean;
  activity_show_final_score: boolean;
  activity_one_attempt: boolean;
}) {
  sendTeacherAmplitudeEvent(EventTypes.LaunchQuiz, data);
}

function sendLaunchSpaceRaceEvent(data = {}) {
  sendTeacherAmplitudeEvent(EventTypes.LaunchSpaceRace, data);
}

function sendLaunchShortAnswerEvent(data = {}) {
  sendTeacherAmplitudeEvent(EventTypes.LaunchShortAnswer, data);
}

function sendLaunchMultipleChoiceEvent(data = {}) {
  sendTeacherAmplitudeEvent(EventTypes.LaunchMultipleChoice, data);
}

function sendLaunchTrueFalseEvent(data = {}) {
  sendTeacherAmplitudeEvent(EventTypes.LaunchTrueFalse, data);
}

function sendLaunchExitTicketEvent(data = {}) {
  sendTeacherAmplitudeEvent(EventTypes.LaunchExitTicket, data);
}

function sendLaunchVoteEvent(data = {}) {
  sendTeacherAmplitudeEvent(EventTypes.LaunchVote, data);
}

interface ActivityEventAdapterParams {
  room_type: RoomTypes;
  quiz_type: 'instant_feedback' | 'open_navigation' | 'teacher_paced';
  activity_require_names: boolean;
  activity_shuffle_questions: boolean;
  activity_shuffle_answers: boolean;
  activity_show_question_feedback: boolean;
  activity_show_final_score: boolean;
  activity_one_attempt: boolean;
}

interface PauseActivityEventAdapterParams extends ActivityEventAdapterParams {
  action_type: 'pause' | 'resume';
}

function sendFinishActivityEvent(data: ActivityEventAdapterParams) {
  sendTeacherAmplitudeEvent(EventTypes.FinishActivity, data);
}

function sendPauseActivityEvent(data: PauseActivityEventAdapterParams) {
  if (data.action_type === 'pause') {
    sendTeacherAmplitudeEvent(EventTypes.PauseActivity, data);
  } else if (data.action_type === 'resume') {
    sendTeacherAmplitudeEvent(EventTypes.ResumeActivity, data);
  }
}

function sendQuestionViewClickEvent(data: {
  question_type: QuestionTypesUtils;
  activity_state: ActivityState;
}) {
  sendTeacherAmplitudeEvent(EventTypes.QuestionViewClick, data);
}

function sendQuestionSavedEvent(data: {
  is_new_question: boolean;
  question_type: QuestionTypes;
  original_question_weight: number;
  saved_question_weight: number;
  original_has_explanation_image: boolean;
  saved_has_explanation_image: boolean;
  original_has_question_image: boolean;
  saved_has_question_image: boolean;
  original_answer_count: number;
  saved_answer_count: number;
}) {
  sendTeacherAmplitudeEvent(EventTypes.QuestionSaved, data);
}

// Quiz events
function sendCreateQuizEvent(data: {
  creation_method:
    | 'app'
    | 'excel'
    | 'code'
    | 'link'
    | 'merge'
    | 'copy'
    | 'from_workspace'
    | 'to_workspace';
  source_workspace_id: string | null;
  destination_workspace_id: string | null;
}) {
  sendTeacherAmplitudeEvent(EventTypes.CreateQuiz, data);
}

function sendCreateFolderEvent(data: { workspace_id?: string }) {
  sendTeacherAmplitudeEvent(EventTypes.CreateFolder, data);
}

// Report events
function sendGenerateReportEvent(data: {
  report_type:
    | 'complete_results'
    | 'individual_students'
    | 'results_summary'
    | 'answer_key';
  delivery_method: 'email' | 'email_to_students' | 'save_to_drive' | 'download';
  option_omit_mc_answer_content: boolean;
  option_include_explanations: boolean;
  options_include_correct_answers: boolean;
  email_to_students_recipient_count;
  activity_student_count: number;
  activity_question_count: number;
  activity_room_type: RoomTypes;
}) {
  sendTeacherAmplitudeEvent(EventTypes.GenerateReport, data);
}

function sendPassRateEvent(data: {
  action: PassRateAction;
  value?: number | null;
}) {
  sendTeacherAmplitudeEvent(EventTypes.PassRate, data);
}

function sendGradeModifiedEvent(data: {
  question_type: QuestionTypes;
  question_weight: number;
  original_correctness: number;
  modified_correctness: number;
  original_score: number;
  modified_score: number;
  activity_type: ActivityType;
  activity_student_count: number;
  activity_question_count: number;
  activity_room_type: RoomTypes;
  activity_end_date: Date;
}) {
  sendTeacherAmplitudeEvent(EventTypes.GradeModified, data);
}

// Paywall / Purchase events
function sendPaywallTouchEvent(data = {}) {
  sendTeacherAmplitudeEvent(EventTypes.PaywallTouch, data);
}

function sendPurchaseEvent(data: {
  id: string;
  license_count: string;
  years: number;
  coupon_code: string;
  price: number;
  referrer: string;
  referrer_paywall_feature: string;
  applied_seat_to_self: boolean;
}) {
  sendTeacherAmplitudeEvent(EventTypes.Purchase, data);
}

function sendApplyLicenseEvent(data = {}) {
  sendTeacherAmplitudeEvent(EventTypes.ApplyLicense, data);
}

function sendContactSalesEvent(data: { form_source: 'typeform' | 'api' }) {
  sendTeacherAmplitudeEvent(EventTypes.ContactSales, data);
}

// Workspace events
function sendCreateWorkspaceEvent(data: { workspace_id: string }) {
  sendTeacherAmplitudeEvent(EventTypes.CreateWorkspace, data);
}

function sendJoinWorkspaceEvent(data: {
  workspace_id: string;
  join_method?: 'code' | 'link';
}) {
  sendTeacherAmplitudeEvent(EventTypes.JoinWorkspace, data);
}

// AI events
function sendGenerateAIQuestionsEvent(data = {}) {
  sendTeacherAmplitudeEvent(EventTypes.GenerateAIQuestions, data);
}

function sendBulkAddQuestionsEvent(data = {}) {
  sendTeacherAmplitudeEvent(EventTypes.BulkAddQuestions, data);
}

function sendImportFromTextEvent(data = {}) {
  sendTeacherAmplitudeEvent(EventTypes.ImportFromTextWithAI, data);
}

function sendViewInsightParticipantsEvent(data = {}) {
  sendTeacherAmplitudeEvent(EventTypes.ViewInsightParticipants, data);
}

export {
  sendSignUpEvent,
  sendLaunchQuizEvent,
  sendLaunchSpaceRaceEvent,
  sendLaunchShortAnswerEvent,
  sendLaunchMultipleChoiceEvent,
  sendLaunchTrueFalseEvent,
  sendLaunchExitTicketEvent,
  sendLaunchVoteEvent,
  sendFinishActivityEvent,
  sendPauseActivityEvent,
  sendGenerateReportEvent,
  sendPaywallTouchEvent,
  sendPurchaseEvent,
  sendApplyLicenseEvent,
  sendGradeModifiedEvent,
  sendPassRateEvent,
  sendQuestionSavedEvent,
  sendQuestionViewClickEvent,
  sendCreateQuizEvent,
  sendContactSalesEvent,
  sendCreateWorkspaceEvent,
  sendJoinWorkspaceEvent,
  sendCreateFolderEvent,
  sendGenerateAIQuestionsEvent,
  sendBulkAddQuestionsEvent,
  sendImportFromTextEvent,
  sendViewInsightParticipantsEvent,
  // Adapters
  LaunchQuizEventAdapter,
  LaunchSpaceRaceEventAdapter,
  LaunchMultipleChoiceEventAdapter,
  LaunchTrueFalseEventAdapter,
  LaunchShortAnswerEventAdapter,
  LaunchExitTicketEventAdapter,
  LaunchVoteEventAdapter,
  FinishActivityEventAdapter,
  LicensePurchaseEventAdapter,
  LicenseApplyEventAdapter,
  GenerateReportEventAdapter,
  GenerateReportEmailStudentsEventAdapter,
  GradeModifiedEventAdapter,
  PaywallTouchEventAdapter,
  QuestionSavedEventAdapter,
  CreateQuizEventAdapter,
  ContactSalesEventAdapter,
  CreateWorkspaceEventAdapter,
  JoinWorkspaceEventAdapter,
  CreateFolderEventAdapter,
  GenerateAIQuestionsEventAdapter,
  ImportFromTextEventAdapter,
  ExportAnswerKeyEventAdapter,
  ViewInsightParticipantsEventAdapter,
};
